import 'app/Window'

import { HttpError } from '@forto-dev/ui-platform-http'
import { Port } from 'features/Directory'
import { ManualQuoteStore } from 'features/ManualQuotes'
import { History } from 'history'
import { isArray, isNil, pickBy } from 'lodash-es'
import { action } from 'mobx'
import { ErrorService } from 'service/ErrorService'
import { HttpBaseApiWrapper } from 'service/HttpBaseApiWrapper'
import { UIStore } from 'service/UIStore'
import { LocalisationStore } from 'utils/localisation'

import { BookingMeta } from '../Booking/BookingMeta'
import { IQuoteRequest, QuoteRequestJSON, serializeRequest } from '../fromMobx'
import { IncoTermMetadata } from '../Incoterms/incotermsMeta'
import { IQuoteSheet } from '../QuoteSheet/QuoteSheetModel'

interface QuoteServiceProps {
  http: HttpBaseApiWrapper
  uiStore: UIStore
  manualQuoteStore: ManualQuoteStore
  errorService: ErrorService
  localisationStore: LocalisationStore
  history: History
}

/**
 * Composes url search string from the params object
 * Arrays are serialised as a multiple praramters (recommended approach)
 * Example: {foo: [1,2]} --> `?foo=1&foo=2`
 */
const getURLSearchString = (params: object) => {
  const u = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (isArray(value)) {
      value.map((item) => u.append(key, item))
    } else {
      u.append(key, value)
    }
  })
  return u.toString()
}

interface IQuoteRequestSuccessData {
  quoteSheetId?: string
  requiresManualQuotation?: boolean
  type?: ManualQuoteStore['type']

  suggestedPort?: Port
  message?: string
}

export class QuoteService {
  private http: HttpBaseApiWrapper
  private uiStore: UIStore
  private manualQuoteStore: ManualQuoteStore
  private errorService: ErrorService
  private localisationStore: LocalisationStore
  private history: History
  private readonly loaderKey = 'QuoteService'

  constructor(props: QuoteServiceProps) {
    this.http = props.http
    this.uiStore = props.uiStore
    this.manualQuoteStore = props.manualQuoteStore
    this.errorService = props.errorService
    this.localisationStore = props.localisationStore
    this.history = props.history
  }

  recentSearches(): Promise<IQuoteSheet[]> {
    return this.http.get<IQuoteSheet[]>('/v1/quotes/recentSearches')
  }

  getIncotermsMetadata(): Promise<IncoTermMetadata[]> {
    return this.http.get<IncoTermMetadata[]>('/v1/quotes/incoterms')
  }

  @action
  search(quoteRequest: IQuoteRequest, noErrorAlert?: boolean, meta?: BookingMeta) {
    this.history.push({ pathname: '/quote-request/search' })
    const quoteRequestJSON = serializeRequest(quoteRequest)
    this.uiStore.loader.show({ key: this.loaderKey, showText: true })

    return this.searchRequest(quoteRequestJSON)
      .then(this.successHandler(quoteRequest, meta), this.responseErrorHandler(quoteRequest, noErrorAlert))
      .catch((err) => {
        return Promise.reject(err)
      })
  }

  searchRequest(quoteRequestJSON: QuoteRequestJSON): Promise<IQuoteRequestSuccessData> {
    return this.http.post('/v1/quotes/search', quoteRequestJSON)
  }

  @action
  successHandler = (quoteRequest: IQuoteRequest, meta?: BookingMeta) => (data: IQuoteRequestSuccessData) => {
    const { uiStore } = this
    // Type should only be present when an error ocurre
    if (data.type && !data.requiresManualQuotation) {
      // If type is present, it means that something went wrong.
      // But if we have a quotesheet id it means that we can still create
      // Manual Quotation
      // The type tells us what went wrong
      uiStore.loader.hide(this.loaderKey)
      return data
    }
    if (data.quoteSheetId) {
      this.processWithQuoteResults(data, quoteRequest, meta)
      // loader hide should be after processing
      // because processing can do history.push
      // if loader.hide will be before, it triggers update of current component if it uses lader.isLoading
      // see Shipment smart component
      uiStore.loader.hide(this.loaderKey)
    } else {
      throw new Error('Unexpected response from the server')
    }
  }

  @action
  processWithQuoteResults(data: IQuoteRequestSuccessData, quoteRequest: IQuoteRequest, meta?: BookingMeta) {
    const quoteRequestJSON = serializeRequest(quoteRequest)

    if (data.requiresManualQuotation) {
      this.manualQuoteStore.triggerManualDialog(quoteRequest, data.quoteSheetId, data.type)
      window.dataLayer.push({
        event: 'quoteRequest',
        eventCategory: 'Quote Request',
        eventAction: 'Manual',
      })
    } else {
      window.dataLayer.push({
        event: 'quoteRequest',
        eventCategory: 'Quote Request',
        eventAction: 'Realtime',
      })
      this.history.push({
        pathname: '/quote-sheet/search',
        search: getURLSearchString({
          request: JSON.stringify(pickBy(quoteRequestJSON, (val) => !isNil(val))),
          ...meta,
        }),
        state: { meta, quoteSheetId: data.quoteSheetId },
      })
    }
  }

  getQuoteOption = (optionID: string) => {
    return this.http.get<IncoTermMetadata[]>(`/v2/quoteService/quoteSheet/${optionID}`)
  }

  @action
  responseErrorHandler = (quoteRequest: IQuoteRequest, noErrorAlert?: boolean) => (err: HttpError) => {
    this.uiStore.loader.hide()
    if (!err.message) {
      err.message = this.localisationStore.translate('quote_request-server_error')
    }
    if (!noErrorAlert && err instanceof HttpError) {
      this.errorService.showErrorDialog(err)
    }
    return Promise.reject(err)
  }

  @action
  showManualQuotePopup() {
    this.manualQuoteStore.status = 'inprogress'
    this.manualQuoteStore.shown = true
  }
}
